import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Trash2, AlertTriangle, MapPin, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

export default function OnsiteSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const [onsite, setOnsite] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    category: 'emergency',
    title: '',
    contact_name: '',
    phone: '',
    location: '',
    details: ''
  });

  useEffect(() => {
    loadOnsite();
  }, [eventId]);

  const loadOnsite = async () => {
    const { data } = await supabase
      .from('onsite_info')
      .select('*')
      .eq('event_id', eventId)
      .order('category, title');
    if (data) setOnsite(data);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await supabase
        .from('onsite_info')
        .update(formData)
        .eq('id', editingId);
    } else {
      await supabase
        .from('onsite_info')
        .insert({ ...formData, event_id: eventId });
    }
    resetForm();
    loadOnsite();
  };

  const deleteEntry = async (id: string) => {
    await supabase.from('onsite_info').delete().eq('id', id);
    setDeleteId(null);
    loadOnsite();
  };

  const resetForm = () => {
    setFormData({
      category: 'emergency',
      title: '',
      contact_name: '',
      phone: '',
      location: '',
      details: ''
    });
    setShowForm(false);
    setEditingId(null);
  };

  const editEntry = (entry: any) => {
    setFormData(entry);
    setEditingId(entry.id);
    setShowForm(true);
  };

  const categories = [
    { value: 'emergency', label: 'Emergency Contacts', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
    { value: 'local', label: 'Local Information', color: 'bg-primary-500/10 text-primary-400 border-primary-500/20' },
    { value: 'services', label: 'Services', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' }
  ];

  const getCategoryInfo = (category: string) => {
    return categories.find(c => c.value === category) || categories[0];
  };

  const groupedOnsite = categories.map(cat => ({
    ...cat,
    items: onsite.filter(o => o.category === cat.value)
  })).filter(cat => cat.items.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">On-Site Information</h2>
          <p className="text-zinc-500 mt-1">Emergency contacts and local resources</p>
        </div>
        {permissions.canEdit && (
          <Button onClick={() => setShowForm(!showForm)} className="bg-white text-zinc-900 hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />
            Add Info
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Edit' : 'Add'} On-Site Information
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category" className="text-zinc-300">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="title" className="text-zinc-300">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="e.g., Security Office"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name" className="text-zinc-300">Contact Name</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-zinc-300">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="location" className="text-zinc-300">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Main Lobby, Room 101"
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label htmlFor="details" className="text-zinc-300">Additional Details</Label>
              <Textarea
                id="details"
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                rows={3}
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" className="bg-white text-zinc-900 hover:bg-zinc-200">
                {editingId ? 'Update' : 'Add'} Info
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm} className="text-zinc-400 hover:text-white">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-zinc-500">Loading...</div>
      ) : onsite.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-8 text-center">
          <p className="text-zinc-500">No on-site information added yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedOnsite.map((category) => (
            <div key={category.value}>
              <h3 className="text-lg font-semibold text-white mb-3">{category.label}</h3>
              <div className="space-y-3">
                {category.items.map((info) => {
                  const catInfo = getCategoryInfo(info.category);
                  return (
                    <div key={info.id} className={`rounded-xl p-5 border ${catInfo.color.includes('red') ? 'bg-red-500/5' : 'bg-zinc-900/50'} border-zinc-800/50 hover:border-zinc-700/50 transition-colors`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {info.category === 'emergency' && <AlertTriangle className="w-5 h-5 text-red-400" />}
                            <h4 className="text-lg font-medium text-white">{info.title}</h4>
                            <span className={`px-2 py-0.5 text-xs rounded-full border ${catInfo.color}`}>
                              {catInfo.label}
                            </span>
                          </div>

                          <div className="space-y-2 text-sm">
                            {info.contact_name && (
                              <p className="text-zinc-400">
                                <span className="text-zinc-500">Contact:</span> {info.contact_name}
                              </p>
                            )}
                            {info.phone && (
                              <div className="flex items-center gap-2 text-zinc-400">
                                <Phone className="w-3.5 h-3.5" />
                                <a href={`tel:${info.phone}`} className="hover:text-white transition-colors">
                                  {info.phone}
                                </a>
                              </div>
                            )}
                            {info.location && (
                              <div className="flex items-center gap-2 text-zinc-400">
                                <MapPin className="w-3.5 h-3.5" />
                                {info.location}
                              </div>
                            )}
                            {info.details && (
                              <p className="text-zinc-500 mt-2 pt-2 border-t border-zinc-800">
                                {info.details}
                              </p>
                            )}
                          </div>
                        </div>

                        {permissions.canEdit && (
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => editEntry(info)}
                              className="text-zinc-400 hover:text-white h-8"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteId(info.id)}
                              className="text-zinc-400 hover:text-red-400 h-8 w-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete On-Site Info?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this on-site information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteEntry(deleteId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
